import { startRegistration } from "@simplewebauthn/browser";
import type { SecurityResponse } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { AuthenticatorSetup } from "../components/AuthenticatorSetup";
import { SavedSignature } from "../components/SignatureInput";
import { StaffAlertsPanel } from "../components/StaffAlerts";
import { StaffCalendarPanel } from "../components/StaffCalendar";
import { ApiError, del, errorMessage, get, post } from "../api";
import { Alert, Button, Field, Loading, PageHeader, Panel, PanelHeader, formatDateTime } from "../components/ui";
import { useSession } from "../session";

export function AccountPage() {
  const { me, signOut } = useSession();
  const qc = useQueryClient();
  const security = useQuery({ queryKey: ["security"], queryFn: () => get<SecurityResponse>("/api/me/security"), retry: false });
  const [notice, setNotice] = useState<string | null>(null);

  const addPasskey = useMutation({
    mutationFn: async () => {
      const { options } = await post<{ options: unknown }>("/api/me/passkeys/start");
      let credential;
      try {
        credential = await startRegistration({ optionsJSON: options as never });
      } catch {
        throw new Error("Passkey setup was cancelled.");
      }
      await post("/api/me/passkeys/complete", { credential });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["security"] });
      setNotice("Passkey added. Next time, sign in with Face ID or Touch ID.");
    },
  });

  const removePasskey = useMutation({
    mutationFn: (id: string) => del(`/api/me/passkeys/${encodeURIComponent(id)}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["security"] });
      setNotice("Passkey removed.");
    },
  });

  if (!me) return null;
  const needsReauth = [security.error, addPasskey.error, removePasskey.error].some((e) => e instanceof ApiError && e.code === "reauth_required");
  const error = addPasskey.error ?? removePasskey.error ?? (needsReauth ? null : security.error);

  return (
    <div className="max-w-3xl">
      <PageHeader title="Your account" description={`${me.staff.firstName} ${me.staff.lastName}, ${me.staff.email}`} />
      <div className="mb-4 flex flex-col gap-2">
        {notice && <Alert tone="ok">{notice}</Alert>}
        {error && !needsReauth && <Alert>{errorMessage(error)}</Alert>}
        {needsReauth && (
          <Alert tone="info">
            For security, sign in again to change your passkeys.{" "}
            <button className="font-semibold underline" onClick={signOut}>
              Sign in again
            </button>
          </Alert>
        )}
      </div>

      <div className="flex flex-col gap-6">
        <Panel>
          <PanelHeader
            title="Passkeys"
            description="Sign in with Face ID, Touch ID, or your device's screen lock. Passkeys sync through iCloud Keychain to your other Apple devices."
            action={
              security.data?.available && (
                <Button variant="primary" onClick={() => addPasskey.mutate()} busy={addPasskey.isPending}>
                  Add a passkey
                </Button>
              )
            }
          />
          {security.isLoading ? (
            <div className="px-5">
              <Loading />
            </div>
          ) : !security.data?.available ? (
            <p className="px-5 py-5 text-sm text-muted">{needsReauth ? "Sign in again to see your passkeys." : "Passkeys aren't available in development mode."}</p>
          ) : security.data.passkeys.length === 0 ? (
            <p className="px-5 py-5 text-ink-soft">You don't have a passkey yet. Add one on each device you use for work.</p>
          ) : (
            <ul className="divide-y divide-mist">
              {security.data.passkeys.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
                  <div>
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-sm text-muted">Added {formatDateTime(p.createdAt, me.clinic.timezone)}</div>
                  </div>
                  <Button variant="quiet" onClick={() => removePasskey.mutate(p.id)} disabled={removePasskey.isPending}>
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <MySignature />
        <StaffAlertsPanel staffId={me.staff.id} title="My alerts" description="A text or email when a visit is booked, cancelled (or taken off your schedule), or moved: yours, or (if you can see everyone's visits on Today) all visits at the clinic. Not for bookings you make yourself. Texts go to the mobile on your staff page." />

        <StaffCalendarPanel staffId={me.staff.id} title="My calendar" description="Your visits on your phone's calendar." />

        <Panel>
          <PanelHeader title="Password backup" description="If a passkey isn't available, you sign in with your password plus a second step." />
          <div className="px-5 py-5 text-ink-soft">
            {!security.data?.available ? (
              <p className="text-sm text-muted">{needsReauth ? "Sign in again to see this." : "Not available in development mode."}</p>
            ) : (
              <SecondStep
                mfa={security.data.mfa}
                onChanged={async (msg) => {
                  await qc.invalidateQueries({ queryKey: ["security"] });
                  setNotice(msg);
                }}
              />
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function SecondStep({ mfa, onChanged }: { mfa: SecurityResponse["mfa"]; onChanged: (msg: string) => void }) {
  const [setup, setSetup] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState("");

  const start = useMutation({
    mutationFn: () => post<{ secret: string; otpauthUri: string }>("/api/me/mfa/totp/start"),
    onSuccess: async (d) => {
      setSetup(d);
    },
  });
  const verify = useMutation({
    mutationFn: () => post("/api/me/mfa/totp/verify", { code }),
    onSuccess: () => {
      setSetup(null);
      setCode("");
      onChanged("Authenticator app set up. You'll use it after your password from now on.");
    },
  });
  const switchToEmail = useMutation({
    mutationFn: () => post("/api/me/mfa/email"),
    onSuccess: () => onChanged("Done. We'll email you a code after your password from now on."),
  });
  const error = start.error ?? verify.error ?? switchToEmail.error;

  if (setup) {
    return (
      <div className="flex flex-col gap-4">
        <AuthenticatorSetup secret={setup.secret} uri={setup.otpauthUri} />
        {error && <Alert>{errorMessage(error)}</Alert>}
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            verify.mutate();
          }}
        >
          <Field
            label="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="w-40"
          />
          <Button variant="primary" type="submit" busy={verify.isPending}>
            Confirm
          </Button>
          <Button variant="quiet" type="button" onClick={() => setSetup(null)}>
            Cancel
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {error && <Alert>{errorMessage(error)}</Alert>}
      {mfa.method === "totp" && <p>After your password, you enter a code from your authenticator app.</p>}
      {mfa.method === "email" && <p>After your password, we email you a code.</p>}
      {mfa.method === "none" && <p>You'll set up your second step the next time you sign in with your password.</p>}
      <div className="flex flex-wrap gap-2">
        {mfa.method !== "totp" && (
          <Button onClick={() => start.mutate()} busy={start.isPending}>
            {mfa.method === "email" ? "Use an authenticator app instead" : "Set up an authenticator app"}
          </Button>
        )}
        {mfa.method === "totp" && (
          <Button onClick={() => start.mutate()} busy={start.isPending}>
            Move to a new authenticator app
          </Button>
        )}
        {mfa.method === "totp" && mfa.emailEnabled && (
          <Button variant="quiet" onClick={() => switchToEmail.mutate()} busy={switchToEmail.isPending}>
            Use emailed codes instead
          </Button>
        )}
      </div>
    </div>
  );
}

/** Your signature for school and work notes. */
function MySignature() {
  const qc = useQueryClient();
  const sig = useQuery({ queryKey: ["my-signature"], queryFn: () => get<{ signature: string | null; credentials: string }>("/api/me/signature") });
  return (
    <Panel>
      <PanelHeader title="Your signature" description={`Used on school and work notes you sign${sig.data?.credentials ? `, after your name and "${sig.data.credentials}"` : ""}. Draw it here (on an iPad, with your finger) or upload a PNG.`} />
      <div className="px-5 py-5">
        {sig.isLoading ? <Loading /> : <SavedSignature url="/api/me/signature" signature={sig.data?.signature ?? null} onSaved={() => qc.invalidateQueries({ queryKey: ["my-signature"] })} />}
      </div>
    </Panel>
  );
}
